<template>
  <div class="wrapper">
    <div class="px-2">
      <data-filter :on-apply-filter="onApplyFilter" hide-report download-excel :on-download-excel="onDownloadExcel"
        :minute=true />
    </div>

    <!-- start unit selection -->
    <div class="wrapper mt-2">
      <div class="d-flex align-items-center">
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Speed Unit</span>
          <select name="" class="form-control" v-model="unit.speed">
            <option value="knot">KNOT</option>
            <option value="km/h">KM/H</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Distance Unit</span>
          <select name="" class="form-control" v-model="unit.distance">
            <option value="nm">NM</option>
            <option value="km">KM</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Sort Data By</span>
          <select name="" class="form-control" v-model="dataSort">
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
          </select>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">&nbsp;</span>
          <button class="btn btn-mantraz" @click="showShowHideModal">
            Filter Column
          </button>
        </div>
        <div class="ml-3">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">&nbsp;</span>
          <button class="btn btn-mantraz" @click="resetColumns">
            Load Default Column
          </button>
        </div>
        <div class="ml-3" v-if="isSuperAdmin">
          <span class="d-block text-muted small text-left font-weight-bold mb-2">Anomaly Highlight</span>
          <button class="btn" :class="highlightAnomaly ? 'btn-mantraz' : 'btn-outline-mantraz'"
            @click="highlightAnomaly = !highlightAnomaly">
            {{ highlightAnomaly ? 'On' : 'Off' }}
          </button>
        </div>
      </div>
    </div>
    <!-- end unit selection -->

    <!-- show/hide column modal -->
    <b-modal id="show-hide-column-modal" title="Show/Hide Datalog Column" size="xl">
      <div class="d-flex justify-content-end mb-3 border-bottom pb-3">
        <button class="btn btn-sm btn-mantraz mr-2" @click="showAllColumns">Show All</button>
        <button class="btn btn-sm btn-outline-mantraz" @click="hideAllColumns">Hide All</button>
      </div>

      <div class="row">
        <div v-for="(categoryColumns, categoryKey) in columnsForModal" :key="categoryKey"
          class="col-md-4 col-lg-3 mb-4">
          <div class="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2">
            <h5 class="text-capitalize font-weight-bold mb-0">{{ categoryLabel(categoryKey) }}</h5>
            <div class="form-check">
              <input type="checkbox" class="form-check-input" :id="`toggle-${categoryKey}`"
                :checked="isCategoryChecked(categoryKey)" @change="toggleCategory(categoryKey)" />
              <label class="form-check-label font-weight-bold small" :for="`toggle-${categoryKey}`">
                Select All
              </label>
            </div>
          </div>
          <div v-for="column in categoryColumns" :key="column.key" class="form-check">
            <input class="form-check-input" type="checkbox" :id="column.key"
              :checked="findColumnInDisplay(column.key).visible" @change="toggleColumnVisibility(column.key)" />
            <label class="form-check-label" :for="column.key">
              {{ column.label }}
            </label>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- ./ show/hide column modal -->

    <template v-if="isLoading">
      <div class="text-center mt-5">
        <b-spinner label="Spinning"></b-spinner>
      </div>
    </template>
    <template v-else-if="currAssetSummary && !isLoading">
      <div class="iq-card mt-3">
        <div class="iq-card-body pt-2 pb-5 mb-5">
          <div class="table-responsive">
            <div style="height: 70vh; overflow-y: auto;">
              <table class="table table-striped table-sm table-hover table-bordered text-center">
                <thead class="thead-light">
                  <tr>
                    <th v-for="(col, index) in visibleColumns" :key="col.key"
                      :style="{ minWidth: col.minWidth * 1.5 + 'px' }" draggable="true" @dragstart="onDragStart(index)"
                      @dragover.prevent @drop="onDrop(index)" @dragend="onDragEnd" class="draggable-column py-2 px-2">
                      {{ col.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in pageRows" :key="data.timestamp" :class="data.rowClass">
                    <td v-for="(col, index) in visibleColumns" :key="col.key"
                      :class="index === 0 ? 'sticky-column' : ''">
                      {{ data[col.key] }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Navigasi halaman (paginate per time-window) -->
          <div class="d-flex align-items-center justify-content-between mt-3 mb-3 px-1" v-if="page.totalPages > 0">
            <span class="text-muted small">{{ pageRangeLabel }}</span>
            <div class="d-flex align-items-center">
              <button class="btn btn-sm btn-outline-mantraz" :disabled="page.safePage <= 0" @click="prevPage">
                Prev
              </button>
              <span class="mx-3 small font-weight-bold">Halaman {{ page.safePage + 1 }} dari {{ page.totalPages }}</span>
              <button class="btn btn-sm btn-outline-mantraz" :disabled="page.safePage >= page.totalPages - 1"
                @click="nextPage">
                Next
              </button>
            </div>
          </div>
          <hr />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
/* eslint-disable object-curly-newline */
import moment from 'moment';
import { mapState } from 'vuex';
import DataFilter from '@/components/DataFilter';
import { toHHMMSS, formatDateTime } from '@/util/date';
import { buildFmsSeries, getRpmTypeLabel } from '@/util/fms-summary';
import paginate from '@/util/paginate';
import { prettyLabel } from '@/util/vesselDetail';
import { initAssetSummary, applyAssetSummaryFilter } from '@/util/asset-summary-loader';
import isAeConsumptionHidden from '@/util/asset-feature-flags';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export default {
  name: 'FmsReportDatalog',
  components: {
    DataFilter,
  },
  data() {
    return {
      unit: {
        distance: 'nm',
        speed: 'knot',
      },
      dataSort: 'oldest',
      displayColumns: [],
      defaultColumns: {},
      columns: {},
      masks: {
        input: 'DD-MM-YYYY HH:mm',

      },
      isLoading: true,
      appliedDateRange: null,
      appliedInterval: null,
      appliedMassId: null,
      isUpdatingColumns: false, // Flag to prevent saving during column update
      highlightAnomaly: false, // Toggle highlight baris anomali RPM vs FLOW (super admin), default off
      currentPage: 0, // Halaman aktif datalog (paginate per time-window)
    };
  },

  computed: {

    currAsset() {
      const currMassId = this.appliedMassId || this.$store.getters['filter/currentMassId'];
      const assets = this.$store.getters['asset/assets'];
      if (!assets) return null;
      return assets.find((asset) => asset.massId === currMassId);
    },
    deviceStatus() {
      return this.currAsset?.deviceStatus;
    },
    isAeConsHidden() {
      return isAeConsumptionHidden(this.currAsset?.massId);
    },
    flowmeterGroups() {
      return this.deviceStatus?.flowmeterGroups || [];
    },
    sortedRpmTypes() {
      return this.sortPositions(this.deviceStatus?.rpmTypes || []);
    },
    exhaustPositions() {
      const details = this.currAsset?.summary?.temperature?.summary?.tempDetails?.EXHAUST_TEMPERATURE || {};
      return this.sortPositions(Object.keys(details));
    },
    tankPositions() {
      const details = this.currAsset?.summary?.temperature?.summary?.tempDetails?.TEMPERATURE || {};
      return this.sortPositions(Object.keys(details));
    },
    currAssetSummary() {
      if (!this.currAsset?.summary) return null;
      const effectiveDateRange = this.appliedDateRange || this.dateRange;
      const effectiveInterval = this.appliedInterval || this.interval;

      return {
        series: buildFmsSeries({
          summary: this.currAsset.summary,
          dateRange: effectiveDateRange,
          interval: effectiveInterval,
          includeTemperature: true,
          temperatureKey: 'temperatureData',
          roundingStrategy: 'interval',
          normalizeHourlyTimestamps: effectiveInterval === 'hour',
          dataSort: this.dataSort,
        }),
      };
    },
    rowsPerPage() {
      // Ukuran halaman per time-window: minute=3 jam, hour=1 hari, day=1 bulan, week=1 tahun.
      const map = { minute: 180, hour: 24, day: 31, week: 52 };
      return map[this.appliedInterval || this.interval] || 180;
    },
    fullSeries() {
      return this.currAssetSummary?.series || [];
    },
    page() {
      // Satu sumber hitungan paging (start/end/totalPages/safePage), udah di-clamp.
      return paginate(this.fullSeries.length, this.rowsPerPage, this.currentPage);
    },
    pagedSeries() {
      return this.fullSeries.slice(this.page.start, this.page.end);
    },
    pageRows() {
      // Baris yang di-render: cuma halaman aktif, biar build berat nggak jalan utk semua data.
      return this.buildRows(this.pagedSeries);
    },
    pageRangeLabel() {
      const rows = this.pageRows;
      if (!rows.length) return '';
      return `${rows[0].dateTime} → ${rows[rows.length - 1].dateTime}`;
    },
    dateRange: {
      get() {
        const { start, end } = this.$store.getters['filter/date'];
        const startDate = new Date(moment(start).valueOf());
        const endDate = new Date(moment(end).valueOf());
        return { start: startDate, end: endDate };
      },
      set(val) {
        const start = moment(val.start);
        const end = moment(val.end);
        this.$store.commit('filter/SET_DATE', { start, end });
      },
    },
    ...mapState('asset', ['assets']),
    ...mapState('filter', {
      currentMassId: (state) => state.currentMassId,
      interval: (state) => state.interval,
      rangeType: (state) => state.rangeType,
    }),
    selectedInterval: {
      get() {
        return this.interval;
      },
      set(val) {
        this.$store.dispatch('filter/setInterval', val);
      },
    },
    selectedAsset: {
      get() {
        return this.currentMassId;
      },
      set(val) {
        this.$store.dispatch('filter/setCurrentMassId', val);
      },
    },
    selectedRangeType: {
      get() {
        return this.rangeType;
      },
      set(val) {
        this.$store.dispatch('filter/setRangeType', val);
      },
    },
    dataColumns() {
      // Use displayColumns to ensure exported data matches current column configuration
      return this.displayColumns;
    },
    columnsForModal() {
      // eslint-disable-next-line no-unused-vars
      const { dateTime, ...rest } = this.defaultColumns;
      return rest;
    },
    visibleColumns() {
      return this.displayColumns
        .filter((col) => col.visible)
        .map((col) => {
          // Satuan Speed & Cruise mengikuti dropdown unit yang dipilih user
          if (col.key === 'speed') {
            return { ...col, label: `Speed (${this.unit.speed})` };
          }
          if (col.key === 'cruise') {
            return { ...col, label: `Cruise (${this.unit.distance})` };
          }
          return col;
        });
    },
  },

  watch: {
    deviceStatus: {
      handler() {
        this.updateColumns();
      },
      deep: true,
    },
    displayColumns: {
      handler(newColumns) {
        // Only save if not currently updating columns
        if (!this.isUpdatingColumns) {
          this.saveColumns(newColumns);
        }
      },
      deep: true,
    },
    currentMassId: {
      handler(newMassId, oldMassId) {
        // Reload columns when switching assets
        if (newMassId && newMassId !== oldMassId) {
          this.appliedMassId = newMassId;
          this.currentPage = 0; // balik ke halaman awal pas ganti asset
          this.updateColumns();
          // Wait for updateColumns to complete before loading saved columns
          this.$nextTick(() => {
            this.loadColumnsSuppressed();
          });
        }
      },
    },
    dataSort() {
      // Urutan berubah (latest/oldest) -> series ke-rebuild, balik ke halaman awal.
      this.currentPage = 0;
    },
  },

  methods: {

    // Bangun array row dari list series. Diparametrik biar build berat (toFixed, DMS,
    // anomaly check) cuma jalan di halaman aktif (pageRows) — full cuma pas export.
    buildRows(seriesList) {
      const data = [];
      const { aeCount = 0 } = this.deviceStatus || {};

      if (!seriesList || !seriesList.length) {
        return data;
      }

      const rpmTypes = this.sortedRpmTypes;
      const fmGroups = this.flowmeterGroups;
      const { exhaustPositions, tankPositions } = this;
      const highlightOn = this.isSuperAdmin && this.highlightAnomaly;

      seriesList.forEach((series) => {
        const row = {
          timestamp: series.timestamp,
          dateTime: formatDateTime(series.timestamp * 1000, 'DD-MM-YYYY, HH:mm'),
          coordinate: typeof series?.gpsData?.latitude !== 'undefined' && typeof series?.gpsData?.longitude !== 'undefined'
            ? `${this.convertDecimalToDMS(series?.gpsData?.latitude, true)}, ${this.convertDecimalToDMS(series?.gpsData?.longitude, false)}`
            : '-',
          firstCoordinate: typeof series?.gpsData?.latitude !== 'undefined' && typeof series?.gpsData?.longitude !== 'undefined'
            ? `${this.convertDecimalToDMS(series?.gpsData?.latitude, true)}, ${this.convertDecimalToDMS(series?.gpsData?.longitude, false)}`
            : '-',
          lastCoordinate: typeof series?.gpsData?.additional?.LAST_dlgpLatitude_4 !== 'undefined' && typeof series?.gpsData?.additional?.LAST_dlgpLongitude_5 !== 'undefined'
            ? `${this.convertDecimalToDMS(series.gpsData.additional.LAST_dlgpLatitude_4, true)}, ${this.convertDecimalToDMS(series.gpsData.additional.LAST_dlgpLongitude_5, false)}`
            : '-',
          speed: this.getSpeed(series),
          heading: typeof series?.gpsData?.track !== 'undefined' ? series?.gpsData?.track.toFixed(2) : '-',
          cruise: this.getCruise(series),
          // Wave Height & Period: rata-rata agregat per interval (samakan pola dgn cruise yg pakai additional)
          waveHeight: typeof series?.gpsData?.additional?.AVG_dlgpWaveHeight_9 === 'number'
            ? series.gpsData.additional.AVG_dlgpWaveHeight_9.toFixed(2)
            : '-',
          wavePeriod: typeof series?.gpsData?.additional?.AVG_dlgpWavePeriod_13 === 'number'
            ? series.gpsData.additional.AVG_dlgpWavePeriod_13.toFixed(2)
            : '-',
          // Arah/kondisi gelombang: nilai representatif, bisa null utk device tanpa data gelombang
          waveDir: typeof series?.gpsData?.waveDir === 'number'
            ? series.gpsData.waveDir.toFixed(2)
            : '-',
          relDir: typeof series?.gpsData?.relDir === 'number'
            ? series.gpsData.relDir.toFixed(2)
            : '-',
          wisScore: typeof series?.gpsData?.wisScore === 'number'
            ? series.gpsData.wisScore
            : '-',
          seaState: series?.gpsData?.seaState
            ? String(series.gpsData.seaState).toUpperCase()
            : '-',

        };

        // RPM per engine (RPM + Running Hours). Nilai RPM disimpan utk cek anomali.
        const rpmByType = {};
        rpmTypes.forEach((rpmType) => {
          const key = rpmType.toLowerCase();
          const rpmTypeUpper = rpmType.toUpperCase();
          const rpmData = series?.rpmData?.[rpmTypeUpper] || null;
          rpmByType[rpmTypeUpper] = typeof rpmData?.rpm === 'number' ? rpmData.rpm : null;
          row[`${key}Rpm`] = typeof rpmData?.rpm !== 'undefined' ? rpmData.rpm.toFixed(2) : '-';
          row[`${key}RunningHours`] = typeof rpmData?.additional?.COUNTGT_dlrpRpm_1 !== 'undefined'
            ? toHHMMSS(rpmData.additional.COUNTGT_dlrpRpm_1 * 60)
            : '-';
        });

        // Flowmeter per group (label-driven, lepas dari RPM). Net flow disimpan per nama group.
        const netFlowByName = {};
        fmGroups.forEach((group) => {
          const { name } = group;
          const key = name.toLowerCase();
          const inKey = `${name}_IN`;
          const fmDataIn = series?.fmData?.[inKey] || null;

          if (group.hasOut) {
            const outKey = `${name}_OUT`;
            const fmDataOut = series?.fmData?.[outKey] || null;
            row[`${key}InVolumeFlowrate`] = typeof fmDataIn?.volumeFlowrate !== 'undefined' ? fmDataIn.volumeFlowrate.toFixed(2) : '-';
            row[`${key}InTemperature`] = typeof fmDataIn?.temperature !== 'undefined' ? fmDataIn.temperature.toFixed(2) : '-';
            row[`${key}InDensity`] = typeof fmDataIn?.density !== 'undefined' ? fmDataIn.density.toFixed(2) : '-';
            row[`${key}OutVolumeFlowrate`] = typeof fmDataOut?.volumeFlowrate !== 'undefined' ? fmDataOut.volumeFlowrate.toFixed(2) : '-';
            row[`${key}OutTemperature`] = typeof fmDataOut?.temperature !== 'undefined' ? fmDataOut.temperature.toFixed(2) : '-';
            row[`${key}OutDensity`] = typeof fmDataOut?.density !== 'undefined' ? fmDataOut.density.toFixed(2) : '-';

            // FLOW = Net (IN - OUT); OUT yang kosong dianggap 0
            const inFlow = typeof fmDataIn?.volumeFlowrate === 'number' ? fmDataIn.volumeFlowrate : null;
            const outFlow = typeof fmDataOut?.volumeFlowrate === 'number' ? fmDataOut.volumeFlowrate : 0;
            netFlowByName[name.toUpperCase()] = inFlow === null ? null : inFlow - outFlow;
          } else {
            row[`${key}VolumeFlowrate`] = typeof fmDataIn?.volumeFlowrate !== 'undefined' ? fmDataIn.volumeFlowrate.toFixed(2) : '-';
            row[`${key}VolumeInventory`] = typeof fmDataIn?.additional?.FIRST_dlfmVolInventory_0 !== 'undefined'
              ? fmDataIn.additional.FIRST_dlfmVolInventory_0.toFixed(2)
              : '-';
            row[`${key}Temperature`] = typeof fmDataIn?.temperature !== 'undefined' ? fmDataIn.temperature.toFixed(2) : '-';
            row[`${key}Density`] = typeof fmDataIn?.density !== 'undefined' ? fmDataIn.density.toFixed(2) : '-';

            netFlowByName[name.toUpperCase()] = typeof fmDataIn?.volumeFlowrate === 'number' ? fmDataIn.volumeFlowrate : null;
          }
        });

        // Exhaust & reservoir temp per posisi (data-driven, lepas dari RPM).
        exhaustPositions.forEach((pos) => {
          const grp = series?.temperatureData?.EXHAUST_TEMPERATURE?.[pos];
          const t = grp && grp[0] ? grp[0] : null;
          row[`temperatureExhaust${pos}`] = typeof t?.additional?.AVG_dltmTemp_2 !== 'undefined'
            ? t.additional.AVG_dltmTemp_2.toFixed(2)
            : '-';
        });
        tankPositions.forEach((pos) => {
          const grp = series?.temperatureData?.TEMPERATURE?.[pos];
          const t = grp && grp[0] ? grp[0] : null;
          row[`temperatureTank${pos}`] = typeof t?.additional?.AVG_dltmTemp_2 !== 'undefined'
            ? t.additional.AVG_dltmTemp_2.toFixed(2)
            : '-';
        });

        // Anomali RPM vs FLOW: cuma utk engine yang punya pasangan (RPM & flowmeter nama sama).
        let hasRpmNoFlow = false; // RPM > 0 tapi FLOW = 0
        let hasFlowNoRpm = false; // RPM = 0 tapi FLOW > 0
        rpmTypes.forEach((rpmType) => {
          const up = rpmType.toUpperCase();
          const rpmValue = rpmByType[up];
          const flowValue = netFlowByName[up];
          if (rpmValue !== null && rpmValue !== undefined && flowValue !== null && flowValue !== undefined) {
            const rpmRounded = Math.round(rpmValue * 100) / 100;
            const flowRounded = Math.round(flowValue * 100) / 100;
            if (rpmRounded > 0 && flowRounded === 0) hasRpmNoFlow = true;
            if (rpmRounded === 0 && flowRounded > 0) hasFlowNoRpm = true;
          }
        });
        // Tandai baris (super admin + toggle aktif). RPM>0 & FLOW 0 lebih kritis -> diprioritaskan.
        if (highlightOn && hasRpmNoFlow) {
          row.rowClass = 'anomaly-rpm-no-flow';
        } else if (highlightOn && hasFlowNoRpm) {
          row.rowClass = 'anomaly-flow-no-rpm';
        } else {
          row.rowClass = '';
        }

        for (let i = 1; i <= aeCount; i += 1) {
          const aeKey = `AE${i}`;
          const aeData = series && series.aeData ? series.aeData[aeKey] : null;
          row[`ae${i}RunningHours`] = typeof aeData?.runningSeconds !== 'undefined'
            ? toHHMMSS(aeData.runningSeconds)
            : '-';
          row[`ae${i}FuelConsumption`] = typeof aeData?.fuelConsumption !== 'undefined'
            ? aeData.fuelConsumption.toFixed(2)
            : '-';
        }

        if (series.powermeterData) {
          Object.keys(series.powermeterData).forEach((pmDeviceKey) => {
            const pmData = series.powermeterData[pmDeviceKey];
            const pmKey = pmDeviceKey.toLowerCase();

            row[`${pmKey}ActivePower`] = typeof pmData?.activePower !== 'undefined' ? pmData.activePower.toFixed(2) : '-';
            row[`${pmKey}ApparentPower`] = typeof pmData?.apparentPower !== 'undefined' ? pmData.apparentPower.toFixed(2) : '-';
            row[`${pmKey}TotalEnergy`] = typeof pmData?.totalEnergy !== 'undefined' ? pmData.totalEnergy.toFixed(2) : '-';
            row[`${pmKey}VoltageLtoL`] = typeof pmData?.voltageLtoL !== 'undefined' ? pmData.voltageLtoL.toFixed(2) : '-';
            row[`${pmKey}VoltageLtoN`] = typeof pmData?.voltageLtoN !== 'undefined' ? pmData.voltageLtoN.toFixed(2) : '-';
            row[`${pmKey}Current`] = typeof pmData?.current !== 'undefined' ? pmData.current.toFixed(2) : '-';
            row[`${pmKey}Frequency`] = typeof pmData?.frequency !== 'undefined' ? pmData.frequency.toFixed(2) : '-';
            row[`${pmKey}PowerFactor`] = typeof pmData?.powerFactor !== 'undefined' ? pmData.powerFactor.toFixed(2) : '-';
            row[`${pmKey}RunningSeconds`] = typeof pmData?.runningSeconds !== 'undefined' ? toHHMMSS(pmData.runningSeconds) : '-';
          });
        }

        data.push(row);
      });

      return data;
    },

    prevPage() {
      if (this.page.safePage > 0) this.currentPage = this.page.safePage - 1;
    },
    nextPage() {
      if (this.page.safePage < this.page.totalPages - 1) this.currentPage = this.page.safePage + 1;
    },

    updateColumns() {
      // Set flag to prevent watcher from saving during update
      this.isUpdatingColumns = true;

      const { aeCount = 0 } = this.deviceStatus || {};
      const rpmTypes = this.sortedRpmTypes;

      const columns = {
        dateTime: [
          { key: 'dateTime', label: 'Date Time', minWidth: 180, visible: true },
        ],

        // Coordinate: interval minute = 1 titik per baris jadi "Coordinate" tunggal cukup;
        // interval lebih besar (hour/day/dst) tiap baris ngerangkum banyak titik, jadi dipisah First/Last
        // biar gak kehilangan posisi akhir bucket.
        gps: (this.appliedInterval || this.interval) === 'minute' ? [
          { key: 'coordinate', label: 'Coordinate', minWidth: 200, visible: true },
          { key: 'heading', label: 'Heading (°)', minWidth: 100, visible: true },
          // Speed & Cruise: label diberi satuan dinamis di computed visibleColumns (ikut dropdown unit)
          { key: 'speed', label: 'Speed', minWidth: 100, visible: true },
          { key: 'cruise', label: 'Cruise', minWidth: 100, visible: true },
        ] : [
          { key: 'firstCoordinate', label: 'First Coordinate', minWidth: 200, visible: true },
          { key: 'lastCoordinate', label: 'Last Coordinate', minWidth: 200, visible: true },
          { key: 'heading', label: 'Heading (°)', minWidth: 100, visible: true },
          { key: 'speed', label: 'Speed', minWidth: 100, visible: true },
          { key: 'cruise', label: 'Cruise', minWidth: 100, visible: true },
        ],

        // Data gelombang & marine jadi kategori sendiri biar gampang difilter.
        // Wave Height & Period pakai rata-rata agregat per interval (AVG_dlgp*);
        // arah & kondisi pakai nilai representatif dari gpsData (bisa null utk device non-gelombang).
        wave: [
          { key: 'waveHeight', label: 'Wave Height (m)', minWidth: 120, visible: true },
          { key: 'waveDir', label: 'Wave Dir (°)', minWidth: 110, visible: true },
          { key: 'wavePeriod', label: 'Wave Period (s)', minWidth: 120, visible: true },
          { key: 'relDir', label: 'Relative Dir (°)', minWidth: 120, visible: true },
          { key: 'wisScore', label: 'WIS Score', minWidth: 100, visible: true },
          { key: 'seaState', label: 'Sea State', minWidth: 110, visible: true },
        ],
      };

      // RPM per engine (RPM + Running Hours). Flow & temp dipisah ke kategori sendiri.
      rpmTypes.forEach((rpmType) => {
        const key = rpmType.toLowerCase();
        // Helper label (CENTER -> MAIN kalau cuma 1 tipe RPM).
        const displayLabel = getRpmTypeLabel(rpmType, rpmTypes);
        columns[key] = [
          { key: `${key}Rpm`, label: `${displayLabel} RPM (rpm)`, minWidth: 100, visible: true },
          { key: `${key}RunningHours`, label: `${displayLabel} Run. Hours`, minWidth: 100, visible: true },
        ];
      });

      // Flowmeter per group (label-driven, tiap line jadi kategori sendiri: fm_<name>).
      this.flowmeterGroups.forEach((group) => {
        const { name } = group;
        const key = name.toLowerCase();
        const lbl = prettyLabel(name);
        const cols = [];
        if (group.hasOut) {
          cols.push({ key: `${key}InVolumeFlowrate`, label: `${lbl} In Flowrate (L/h)`, minWidth: 120, visible: true });
          cols.push({ key: `${key}InTemperature`, label: `${lbl} In Temp. (°C)`, minWidth: 120, visible: true });
          cols.push({ key: `${key}InDensity`, label: `${lbl} In Density (g/cm³)`, minWidth: 130, visible: true });
          cols.push({ key: `${key}OutVolumeFlowrate`, label: `${lbl} Out Flowrate (L/h)`, minWidth: 120, visible: true });
          cols.push({ key: `${key}OutTemperature`, label: `${lbl} Out Temp. (°C)`, minWidth: 120, visible: true });
          cols.push({ key: `${key}OutDensity`, label: `${lbl} Out Density (g/cm³)`, minWidth: 130, visible: true });
        } else {
          cols.push({ key: `${key}VolumeFlowrate`, label: `${lbl} Flowrate (L/h)`, minWidth: 110, visible: true });
          cols.push({ key: `${key}VolumeInventory`, label: `${lbl} Vol. Inv. (L)`, minWidth: 110, visible: true });
          cols.push({ key: `${key}Temperature`, label: `${lbl} Temp. (°C)`, minWidth: 110, visible: true });
          cols.push({ key: `${key}Density`, label: `${lbl} Density (g/cm³)`, minWidth: 120, visible: true });
        }
        columns[`fm_${key}`] = cols;
      });

      // Exhaust temperature per posisi (data-driven, lepas dari RPM).
      if (this.exhaustPositions.length) {
        columns.exhaust = this.exhaustPositions.map((pos) => ({
          key: `temperatureExhaust${pos}`,
          label: `${getRpmTypeLabel(pos, rpmTypes)} Exhaust Temp. (°C)`,
          minWidth: 120,
          visible: true,
        }));
      }

      // Reservoir tank temperature per posisi (data-driven).
      if (this.tankPositions.length) {
        columns.reservoir = this.tankPositions.map((pos) => ({
          key: `temperatureTank${pos}`,
          label: `${getRpmTypeLabel(pos, rpmTypes)} Reservoir Temp. (°C)`,
          minWidth: 120,
          visible: true,
        }));
      }

      if (aeCount > 0) {
        columns.ae = [];
        for (let i = 1; i <= aeCount; i += 1) {
          columns.ae.push({ key: `ae${i}RunningHours`, label: `AE${i} Run. Hours`, minWidth: 100, visible: true });
          if (!this.isAeConsHidden) {
            columns.ae.push({ key: `ae${i}FuelConsumption`, label: `AE${i} Fuel Consumption (L)`, minWidth: 100, visible: true });
          }
        }
      }

      if (this.deviceStatus?.hasPowermeter) {
        columns.powermeter = [];
        const pmDevices = this.currAsset?.summary?.powermeter?.summary?.devices;
        if (pmDevices) {
          Object.keys(pmDevices).forEach((pmDeviceKey) => {
            const pmName = pmDeviceKey.replace('PM_', '').replace('_', ' ');
            const pmKey = pmDeviceKey.toLowerCase();
            columns.powermeter.push({ key: `${pmKey}ActivePower`, label: `${pmName} Active Power (kW)`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}ApparentPower`, label: `${pmName} Apparent Power (kVA)`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}TotalEnergy`, label: `${pmName} Total Energy (kWh)`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}VoltageLtoL`, label: `${pmName} Voltage L-L (V)`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}VoltageLtoN`, label: `${pmName} Voltage L-N (V)`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}Current`, label: `${pmName} Current (A)`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}Frequency`, label: `${pmName} Frequency (Hz)`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}PowerFactor`, label: `${pmName} Power Factor`, minWidth: 120, visible: true });
            columns.powermeter.push({ key: `${pmKey}RunningSeconds`, label: `${pmName} Run. Hours`, minWidth: 120, visible: true });
          });
        }
      }

      this.defaultColumns = JSON.parse(JSON.stringify(columns));
      this.columns = JSON.parse(JSON.stringify(columns));
      this.displayColumns = Object.values(this.defaultColumns).flat();

      // Reset flag after update complete
      this.$nextTick(() => {
        this.isUpdatingColumns = false;
      });
    },
    toHHMMSS,
    getSpeed(series) {
      if (typeof series?.gpsData?.speed === 'undefined') {
        return '-';
      }
      if (this.unit.speed === 'knot') {
        return (series.gpsData.speed * 0.539957).toFixed(2);
      }
      return series.gpsData.speed.toFixed(2);
    },
    getCruise(series) {
      if (typeof series?.gpsData?.additional?.SUM_dlgpDistance_3 === 'undefined') {
        return '-';
      }
      const distance = series.gpsData.additional.SUM_dlgpDistance_3;
      if (this.unit.distance === 'nm') {
        return (distance * 0.000539957).toFixed(2);
      }
      return (distance * 0.001).toFixed(2);
    },

    convertDecimalToDMS(decimal, isLatitude) {
      const degrees = Math.floor(Math.abs(decimal));
      const minutesDecimal = (Math.abs(decimal) - degrees) * 60;
      const minutes = Math.floor(minutesDecimal);
      const seconds = Math.round((minutesDecimal - minutes) * 60);

      let direction;
      if (decimal >= 0) {
        direction = isLatitude ? 'N' : 'E';
      } else {
        direction = isLatitude ? 'S' : 'W';
      }
      return `${degrees}° ${minutes}' ${seconds}'' ${direction}`;
    },
    moment(date) {
      return moment(date);
    },
    sortPositions(arr) {
      const order = ['PORT', 'CENTER', 'STARBOARD'];
      return [...arr].sort((a, b) => {
        const ia = order.indexOf(String(a).toUpperCase());
        const ib = order.indexOf(String(b).toUpperCase());
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
    },
    categoryLabel(key) {
      // Heading kategori di modal show/hide. fm_* -> "Flowmeter <pretty>".
      if (key.startsWith('fm_')) return `Flowmeter ${prettyLabel(key.slice(3).toUpperCase())}`;
      const map = {
        gps: 'GPS',
        wave: 'Wave & Marine',
        ae: 'AE',
        powermeter: 'Powermeter',
        exhaust: 'Exhaust Temp',
        reservoir: 'Reservoir Temp',
      };
      if (map[key]) return map[key];
      return key.charAt(0).toUpperCase() + key.slice(1);
    },

    async onApplyFilter() {
      this.isLoading = true;
      try {
        const result = await applyAssetSummaryFilter(this.$store, {
          dateRange: this.dateRange,
          interval: this.selectedInterval || this.interval,
          massId: this.selectedAsset || this.currentMassId,
        });
        this.appliedDateRange = {
          start: new Date(moment(result.dateRange.start).valueOf()),
          end: new Date(moment(result.dateRange.end).valueOf()),
        };
        this.appliedInterval = result.interval;
        this.appliedMassId = result.massId;
        this.currentPage = 0; // filter baru -> balik ke halaman awal
        // Kolom exhaust/reservoir data-driven -> rebuild ngikut data range terbaru,
        // lalu restore layout tersimpan user.
        this.updateColumns();
        await this.$nextTick();
        this.loadColumnsSuppressed();
      } catch (err) {
        console.error(err);
      } finally {
        this.isLoading = false;
      }
    },

    async onDownloadExcel() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Data');

      // Use visibleColumns computed property to ensure export matches current filter
      const visibleColumns = this.visibleColumns;

      // Set header Excel berdasarkan kolom yang visible dan terurut
      worksheet.columns = visibleColumns.map((col) => ({
        header: col.label,
        key: col.key,
        width: 25, // Lebar kolom bisa disesuaikan
      }));

      // Export tetap SEMUA data (semua halaman), bukan cuma halaman aktif -> build full di sini.
      this.buildRows(this.fullSeries).forEach((row) => {
        const rowData = {};
        visibleColumns.forEach((col) => {
          rowData[col.key] = row[col.key] ?? '-';
        });
        worksheet.addRow(rowData);
      });

      const appliedRange = this.appliedDateRange || this.dateRange;
      const filename = `${this.currAsset?.massName} - ${formatDateTime(appliedRange.start, 'DD-MM-YYYY HH:mm')} - ${formatDateTime(appliedRange.end, 'DD-MM-YYYY HH:mm')}`;
      // Menyimpan file Excel
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${filename}.xlsx`);
    },

    showShowHideModal() {
      this.$bvModal.show('show-hide-column-modal');
    },

    hideShowHideModal() {
      this.$bvModal.hide('show-hide-column-modal');
    },

    onDragStart(index) {
      this.dragSrcIndex = index;
    },

    onDrop(targetIndex) {
      if (this.dragSrcIndex === null || this.dragSrcIndex === targetIndex) {
        this.dragSrcIndex = null;
        return;
      }
      if (this.dragSrcIndex === 0 || targetIndex === 0) {
        this.dragSrcIndex = null;
        return;
      }

      // Langsung manipulasi 'displayColumns'
      const draggedCol = this.displayColumns.splice(this.dragSrcIndex, 1)[0];
      this.displayColumns.splice(targetIndex, 0, draggedCol);

      this.dragSrcIndex = null;
    },

    onDragEnd() {
      this.dragSrcIndex = null;
    },

    openIndexedDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('mantraz');

        request.onupgradeneeded = function handleUpgrade(event) {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('columns')) {
            db.createObjectStore('columns', { keyPath: 'id' });
          }
        };

        request.onsuccess = function handleSuccess(event) {
          resolve(event.target.result);
        };

        request.onerror = function handleError(event) {
          const message = `IndexedDB error: ${event?.target?.error?.message || event?.target?.errorCode || 'Unknown error'}`;
          reject(new Error(message));
        };
      });
    },

    getDeviceStructureKey() {
      // Generate storage key based on device structure/configuration
      const {
        aeCount = 0,
        hasExhaustTemperature = false,
        flowmeterGroups = [],
        rpmTypes = [],
        hasPowermeter = false,
      } = this.deviceStatus || {};

      // Sort rpmTypes to ensure consistent key generation
      const sortedRpmTypes = [...rpmTypes].sort().join(',');

      // Get powermeter device keys if exists
      let pmDevicesKey = '';
      if (hasPowermeter && this.currAsset?.summary?.powermeter?.summary?.devices) {
        const pmDevices = Object.keys(this.currAsset.summary.powermeter.summary.devices).sort();
        pmDevicesKey = pmDevices.join(',');
      }

      // Create hash-like key from device structure using simple string concatenation
      const fmGroupsKey = flowmeterGroups
        .map((g) => `${g.name}:${g.hasIn ? 1 : 0}${g.hasOut ? 1 : 0}`)
        .sort()
        .join(',');
      // Kolom GPS beda per interval (minute = Coordinate, selain itu First/Last Coordinate),
      // jadi layout-nya disimpan terpisah biar kolomnya nggak nyasar ke belakang.
      const gpsVariant = (this.appliedInterval || this.interval) === 'minute' ? 'min' : 'agg';
      const structureKey = `${aeCount}|${hasExhaustTemperature}|${fmGroupsKey}|${sortedRpmTypes}|${hasPowermeter}|${pmDevicesKey}|${gpsVariant}`;

      // Simple hash function without bitwise operations
      let hash = 0;
      for (let i = 0; i < structureKey.length; i += 1) {
        hash = ((hash * 31) + structureKey.charCodeAt(i)) % 2147483647;
      }

      const key = `mantraz_datalog_struct_${Math.abs(hash).toString(36)}`;
      return key;
    },

    saveColumns(columns) {
      const storageKey = this.getDeviceStructureKey();

      // Save to localStorage as primary storage (simpler and more reliable for this use case)
      try {
        localStorage.setItem(storageKey, JSON.stringify(columns));
      } catch (err) {
        console.error('Failed to save columns to localStorage:', err.message);
      }

      // Also save to IndexedDB as backup (legacy support)
      this.openIndexedDB().then((db) => {
        const tx = db.transaction('columns', 'readwrite');
        const store = tx.objectStore('columns');
        // Simpan dengan key berdasarkan device structure
        store.put({ id: storageKey, columns });
      }).catch((err) => {
        console.error('Failed to open IndexedDB:', err.message);
      });
    },

    loadColumnsFromIndexedDB() {
      const storageKey = this.getDeviceStructureKey();
      return this.openIndexedDB().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction('columns', 'readonly');
        const store = tx.objectStore('columns');
        const request = store.get(storageKey);

        request.onsuccess = function handleGetSuccess(event) {
          resolve(event.target.result?.columns || null);
        };

        request.onerror = function handleGetError(evt) {
          const message = evt?.target?.error?.message || 'Failed to load columns';
          reject(new Error(message));
        };
      })).catch((err) => {
        console.error('Failed to open IndexedDB:', err.message);
      });
    },

    loadColumnsFromLocalStorage() {
      const storageKey = this.getDeviceStructureKey();
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          return JSON.parse(stored);
        }
        return null;
      } catch (err) {
        console.error('Failed to load columns from localStorage:', err.message);
        return null;
      }
    },

    resetColumns() {
      // Reset 'displayColumns' dengan meratakannya dari 'defaultColumns'
      this.displayColumns = Object.values(this.defaultColumns).flat();
    },

    loadColumnsForCurrentAsset() {
      // Priority: load from localStorage first (faster and simpler)
      const localStorageColumns = this.loadColumnsFromLocalStorage();
      if (localStorageColumns) {
        this.migrateAndSetColumns(localStorageColumns);
        return;
      }

      // Fallback to IndexedDB if localStorage is empty
      this.loadColumnsFromIndexedDB()
        .then((storedColumns) => {
          this.migrateAndSetColumns(storedColumns);
          // Migrate to localStorage for future use
          if (storedColumns) {
            this.saveColumns(storedColumns);
          }
        })
        .catch((err) => {
          console.warn('Could not load columns from IndexedDB:', err.message);
          this.resetColumns();
        });
    },

    loadColumnsSuppressed() {
      // Load layout tersimpan TANPA men-trigger auto-save. Penting karena kolom temp
      // (exhaust/reservoir) data-driven: bisa muncul/ilang tergantung data di range
      // terpilih. Tanpa guard ini, reconcile saat load bisa nimpa layout tersimpan user
      // (kolom yang lagi nggak ada datanya bisa kehapus permanen dari layout).
      this.isUpdatingColumns = true;
      this.loadColumnsForCurrentAsset();
      this.$nextTick(() => {
        this.isUpdatingColumns = false;
      });
    },

    /**
     * Menampilkan semua kolom.
     */
    showAllColumns() {
      // Menambahkan tanda kurung pada argumen arrow function
      Object.values(this.columns).forEach((categoryColumns) => {
        categoryColumns.forEach((col) => {
          col.visible = true;
        });
      });
    },

    /**
     * Menyembunyikan semua kolom, kecuali kolom 'dateTime' yang krusial.
     */
    hideAllColumns() {
      // Menambahkan tanda kurung pada argumen arrow function
      Object.values(this.columns).forEach((categoryColumns) => {
        categoryColumns.forEach((col) => {
          if (col.key !== 'dateTime') {
            col.visible = false;
          }
        });
      });
    },

    /**
     * Memeriksa apakah semua kolom dalam satu kategori sedang ditampilkan.
     * @param {string} categoryKey - Kunci kategori (misal: 'gps', 'port').
     * @returns {boolean} - True jika semua kolom visible.
     */
    isCategoryChecked(categoryKey) {
      const categoryColumns = this.defaultColumns[categoryKey];
      if (!categoryColumns) return false;
      const categoryKeys = categoryColumns.map((c) => c.key);
      return this.displayColumns
        .filter((col) => categoryKeys.includes(col.key))
        .every((col) => col.visible);
    },

    /**
     * Menampilkan atau menyembunyikan semua kolom dalam satu kategori.
     * Jika ada satu saja yang tersembunyi, maka akan menampilkan semua.
     * Jika semua sudah tampil, maka akan menyembunyikan semua.
     * @param {string} categoryKey - Kunci kategori yang akan di-toggle.
     */
    toggleCategory(categoryKey) {
      const categoryColumns = this.defaultColumns[categoryKey];
      if (!categoryColumns) return;

      const categoryColumnKeys = categoryColumns.map((c) => c.key);
      const shouldShow = this.displayColumns
        .filter((c) => categoryColumnKeys.includes(c.key))
        .some((c) => !c.visible);
      this.displayColumns = this.displayColumns.map((col) => {
        if (categoryColumnKeys.includes(col.key)) {
          return { ...col, visible: shouldShow };
        }
        return col;
      });
    },

    /**
     * Memigrasikan struktur kolom lama (array) ke struktur baru (objek)
     * dan mengatur data kolom komponen.
     * @param {Array|Object} loadedColumns - Kolom yang dimuat dari IndexedDB.
     */
    migrateAndSetColumns(loadedColumns) {
      if (!loadedColumns) {
        // Jika tidak ada data tersimpan, gunakan default
        this.resetColumns();
        return;
      }

      // Jika data tersimpan adalah array (struktur baru):
      // pertahankan urutan & visibility dari yang tersimpan, tapi SELALU ambil
      // label & minWidth terbaru dari kode (biar satuan kolom ikut update).
      if (Array.isArray(loadedColumns)) {
        const freshColumns = Object.values(this.defaultColumns).flat();
        const freshMap = new Map(freshColumns.map((col) => [col.key, col]));
        const savedKeys = new Set(loadedColumns.map((col) => col.key));

        const merged = loadedColumns
          .filter((col) => freshMap.has(col.key)) // buang kolom yang sudah tidak ada
          .map((col) => ({ ...freshMap.get(col.key), visible: col.visible }));

        // Tambahkan kolom baru yang belum ada saat setting disimpan
        freshColumns.forEach((col) => {
          if (!savedKeys.has(col.key)) {
            merged.push(col);
          }
        });

        this.displayColumns = merged;
      } else {
        // Jika data tersimpan adalah objek (struktur lama), lakukan migrasi
        console.warn('Old column structure detected. Migrating...');
        const oldVisibilityMap = new Map(Object.values(loadedColumns).flat().map((col) => [col.key, col.visible]));
        const newColumns = JSON.parse(JSON.stringify(Object.values(this.defaultColumns).flat()));
        newColumns.forEach((col) => {
          if (oldVisibilityMap.has(col.key)) {
            // eslint-disable-next-line no-param-reassign
            col.visible = oldVisibilityMap.get(col.key);
          }
        });
        this.displayColumns = newColumns;
      }
    },

    findColumnInDisplay(key) {
      return this.displayColumns.find((c) => c.key === key);
    },

    toggleColumnVisibility(key) {
      const index = this.displayColumns.findIndex((c) => c.key === key);
      if (index !== -1) {
        const newColumns = [...this.displayColumns];
        newColumns[index] = { ...newColumns[index], visible: !newColumns[index].visible };
        this.displayColumns = newColumns;
      }
    },
  },
  async mounted() {
    const date = this.$store.getters['filter/date'];
    await initAssetSummary(this.$store, {
      dateRange: date,
      withDevices: true,
      withLastData: true,
    });

    this.appliedDateRange = {
      start: new Date(moment(date.start).valueOf()),
      end: new Date(moment(date.end).valueOf()),
    };

    this.appliedInterval = this.$store.getters['filter/interval'];
    this.appliedMassId = this.$store.getters['filter/currentMassId'];
    this.updateColumns();

    // Wait for updateColumns to complete before loading saved columns
    await this.$nextTick();
    this.loadColumnsSuppressed();

    this.isLoading = false;
  },
};

</script>

<style scoped>
.flex-container {
  display: flex;
  justify-content: space-between;
}

.flex-item {
  /* padding: 10px 20px; */
  margin: 0 5px;
}

@media screen and (max-width: 600px) {
  .flex-container {
    flex-direction: column;
  }

  .flex-item {
    width: 100%;
  }
}

.sticky-column {
  position: sticky;
  left: 0;
}

.sticky-column:nth-child(odd) {
  background-color: #f5f5f5 !important;
}

.sticky-column:nth-child(even) {
  background-color: #f5f5f5 !important;
}

.draggable-column {
  cursor: move;
  user-select: none;
}

.draggable-column.dragging {
  opacity: 0.5;
}

.table {
  border-collapse: collapse;
}

th {
  position: sticky !important;
  top: 0 !important;
  border: 1px solid !important;
  padding: 8px !important;
  text-align: center !important;
  vertical-align: middle !important;
  font-size: 14.5px !important;
  background-color: #767476 !important;
  color: #FAFAFA !important;
}

th:first-child {
  position: sticky !important;
  left: 0 !important;
  z-index: 5;
}

/* Highlight baris anomali (override striped + sticky column) */
.table tbody tr.anomaly-rpm-no-flow > td {
  background-color: #f8d7da !important; /* RPM > 0 tapi FLOW = 0 (merah muda) */
}

.table tbody tr.anomaly-flow-no-rpm > td {
  background-color: #fff3cd !important; /* RPM = 0 tapi FLOW > 0 (kuning) */
}
</style>
